import { put, select, take, takeLatest } from 'redux-saga/effects';
import { END, eventChannel } from 'redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { ApiResult, FileDetails } from 'common/models';
import { FAILURE_HEADER, FILE_UPLOAD_SUCCESS_MESSAGE, SUCCESS_HEADER } from 'common/config';

import { FileUploadService } from './fileUpload.service';
import { fileUploadActions } from './fileUpload.slice';
import { RootState } from '../core/root.reducer';
import { appStateActions } from '../app-state';
import { MessageType } from 'common/enums';

let emit: any;
const channel: any = eventChannel((emitter: any) => {
    emit = emitter;
    return () => {
        // unmounting function
    };
});

const uploadFile = function* ({
    payload
}: PayloadAction<{
    files: Array<FileDetails>;
    variantName: string;
    targetId: string;
    entityType: string;
}>) {
    const onProgress = (event: any) => {
        const percentCompleted = Math.round((event.loaded / event.total) * 100);
        emit(percentCompleted);
        if (percentCompleted == 100) emit(END);
        else emit(percentCompleted);
    };
    const { hasErrors, errors, value }: ApiResult<any> = yield FileUploadService.uploadFile(
        payload.files[0],
        onProgress,
        payload.variantName,
        payload.targetId,
        payload.entityType
    );
    if (hasErrors && errors) {
        const uploadError = errors[0];
        yield put({
            type: fileUploadActions.uploadFileFailed.type,
            payload: uploadError
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: uploadError,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: fileUploadActions.uploadFileSuccess.type,
            payload: FILE_UPLOAD_SUCCESS_MESSAGE
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: FILE_UPLOAD_SUCCESS_MESSAGE,
                type: MessageType.Success
            }
        });
    }
};

const uploadFileWatch = function* () {
    yield takeLatest(fileUploadActions.uploadFile.type, uploadFile);
};

const uploadProgressWatch = function* (
    evtChannel: any = channel
): Generator<any, void, number | RootState | any> {
    while (true) {
        const data = yield take(evtChannel);
        const state = yield select();
        const uploadingFiles = state.fileUpload.uploadingFiles;
        const updatedFile: FileDetails = new File([uploadingFiles[0]], uploadingFiles[0].name, {
            type: uploadingFiles[0].type
        });
        updatedFile.progress = data;
        const newUploadingFiles = [updatedFile];
        yield put({
            type: fileUploadActions.setUploadingFiles.type,
            payload: newUploadingFiles
        });
    }
};

const getSyncDetails = function* () {
    const { hasErrors, errors, value }: ApiResult<any> = yield FileUploadService.getSyncDetails();
    if (hasErrors && errors) {
        const syncError = errors[0];
        yield put({
            type: fileUploadActions.getSyncDetailsFailed.type,
            payload: syncError
        });
    } else {
        yield put({
            type: fileUploadActions.getSyncDetailsSuccess.type,
            payload: value
        });
    }
};

const getSyncDetailsWatch = function* () {
    yield takeLatest(fileUploadActions.getSyncDetails.type, getSyncDetails);
};

const fetchUploadStatus = function* () {
    const { hasErrors, errors, value } = yield FileUploadService.fetchUploadStatus();
    if (hasErrors && errors) {
        yield put({
            type: fileUploadActions.fetchUploadStatusFailed.type
        });
    } else {
        yield put({
            type: fileUploadActions.fetchUploadStatusSuccess.type,
            payload: value
        });
    }
};

const fetchUploadStatusWatch = function* () {
    yield takeLatest(fileUploadActions.fetchUploadStatus.type, fetchUploadStatus);
};

export { uploadFileWatch, uploadProgressWatch, getSyncDetailsWatch, fetchUploadStatusWatch };
